import {
  Avatar,
  Badge,
  BadgeProps,
  Box,
  IconButton,
  SxProps,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import EditIcon from "../../assets/Icons/EditIcon";
import ProfileCompletenessIcon from "../../assets/Icons/ProfileCompletenessIcon";
import ProfileIcon from "../../assets/Icons/ProfileIcon";
import { VerifiedStatus } from "../../constants/UserConstants";
import UserType from "../../constants/UserType";
import { uploadProfileImage } from "../../firebase/user";
import getProfileCompletenessValue from "../../helper/getProfileCompletenessValue";
import User from "../../interfaces/User";
import { theme } from "../../mui-customs/Theme";
import { setProfilePicture } from "../../redux/userSlice";
import Loader from "../Loader";
import { PhotoUploadModal } from "../photo-upload-modal";

interface ProfileImageProps extends BadgeProps {
  userData: User;
  canEdit?: boolean;
  sx?: SxProps;
}
const ProfileImage = ({
  userData,
  canEdit = true,
  sx,
  ...props
}: ProfileImageProps) => {
  const [profileImage, setProfileImage] = useState(userData.profileImage);
  const [isPhotoEditModalOpen, setIsPhotoEditModalOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setProfileImage(userData.profileImage);
  }, [userData.profileImage]);

  const { value: profileCompletenessValue } =
    getProfileCompletenessValue(userData);

  const handleProfileImageSubmit = async (imageFile: File) => {
    console.log("📸 handleProfileImageSubmit called");
    setImageLoading(true);
    try {
      const url =
        imageFile &&
        userData.uid &&
        (await uploadProfileImage(imageFile, userData.uid));
      url && setProfileImage(url);
      url && dispatch(setProfilePicture(url));
      setImageLoading(false);
    } catch (error) {
      console.error("❌ ERROR in handleProfileImageSubmit:", error);
      toast.error("Something went wrong.");
      setImageLoading(false);
    }
  };

  const handleEditButtonClick = () => {
    setIsPhotoEditModalOpen(true);
  };

  return (
    <Badge
      sx={{
        position: "relative",
        ...sx,
      }}
      overlap="circular"
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      badgeContent={
        canEdit && (
          <IconButton
            onClick={handleEditButtonClick}
            sx={{
              padding: "9px",
              borderRadius: "6px",
              backgroundColor: theme.brand1[500],
              transition: "0.2s",
              ":hover": {
                backgroundColor: theme.brand1[600],
              },
            }}
            component="label"
            aria-label="delete"
          >
            <EditIcon />
          </IconButton>
        )
      }
      {...props}
    >
      {(userData.userType === UserType.Chef
        ? userData.verified === VerifiedStatus.True
        : profileCompletenessValue === 100) && (
        <Box sx={{ position: "absolute", top: 0, left: 0, zIndex: 2 }}>
          <ProfileCompletenessIcon />
        </Box>
      )}
      <Avatar
        sx={{
          height: "133px",
          width: "133px",
          background: theme.whiteBg.WBG3,
        }}
      >
        {imageLoading ? (
          <Loader />
        ) : profileImage ? (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              backgroundImage: `url(${profileImage})`,
              backgroundPosition: "center",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
            }}
          />
        ) : (
          <ProfileIcon />
        )}
      </Avatar>

      <PhotoUploadModal
        open={isPhotoEditModalOpen}
        handleClose={() => {
          setIsPhotoEditModalOpen(false);
        }}
        image={profileImage}
        handleSave={handleProfileImageSubmit}
        height={350}
        width={350}
        borderRadius={175}
      />
    </Badge>
  );
};

export default ProfileImage;
